import config from 'shared/config'
import { FLOW_DATA_AGENT_ID } from './flowDataAgentConstants'

/** Sidenav / page-tools agent (help center + frontend tools). */
export const ALVIE_COPILOT_AGENT_ID = 'alvie_copilot' as const

/** Duplicate of the Flow data agent (`precis_agent_v1`) on its own Agent Engine. */
const ALVIE_AI_AGENT_ID = 'alvie_ai' as const

/** Agents Flow exposes as AG-UI endpoints: what Flow records on a chat and what the switcher lists. */
export const COPILOT_AGENT_NAMES = [ALVIE_COPILOT_AGENT_ID, ALVIE_AI_AGENT_ID, FLOW_DATA_AGENT_ID] as const

export type CopilotAgentName = (typeof COPILOT_AGENT_NAMES)[number]

/** UI surfaces that each own an independent CopilotKit conversation. */
export type CopilotSurface = 'drawer' | 'fullscreen'

/** Production agent per surface; also the agent of chats created before Flow recorded `agent_name`. */
export const SURFACE_DEFAULT_AGENT: Record<CopilotSurface, CopilotAgentName> = {
  drawer: ALVIE_COPILOT_AGENT_ID,
  fullscreen: FLOW_DATA_AGENT_ID
}

/** Dev-only agent switcher; production always runs each surface's default. */
export const IS_AGENT_SWITCHER_ENABLED = ['development', 'staging'].includes(config('APP_ENV') as string)

/**
 * CopilotKit shares one agent object per runtime id and both surfaces stay mounted, so an agent
 * keeps its plain id only on its own surface. `app/api/copilot/route.ts` points every id at the
 * agent's Flow endpoint.
 */
const RUNTIME_AGENT_IDS = {
  alvie_copilot: { drawer: 'alvie_copilot', fullscreen: 'alvie_copilot_fullscreen' },
  alvie_ai: { drawer: 'alvie_ai_drawer', fullscreen: 'alvie_ai' },
  precis_agent_v1: { drawer: 'precis_agent_v1_drawer', fullscreen: 'precis_agent_v1' }
} as const satisfies Record<CopilotAgentName, Record<CopilotSurface, string>>

/** CopilotKit runtime id: the key a surface's conversation binds to. */
export type CopilotAgentId = (typeof RUNTIME_AGENT_IDS)[CopilotAgentName][CopilotSurface]

export const toRuntimeAgentId = (surface: CopilotSurface, agentName: CopilotAgentName): CopilotAgentId =>
  RUNTIME_AGENT_IDS[agentName][surface]

/**
 * Every runtime id the CopilotKit runtime registers, with the Flow agent it calls. Production only
 * ever binds each surface's default, so it registers just those.
 */
export const RUNTIME_AGENTS = IS_AGENT_SWITCHER_ENABLED
  ? COPILOT_AGENT_NAMES.flatMap((agentName) =>
      (['drawer', 'fullscreen'] as const).map((surface) => ({
        runtimeId: toRuntimeAgentId(surface, agentName),
        agentName
      }))
    )
  : (['drawer', 'fullscreen'] as const).map((surface) => ({
      runtimeId: toRuntimeAgentId(surface, SURFACE_DEFAULT_AGENT[surface]),
      agentName: SURFACE_DEFAULT_AGENT[surface]
    }))

/** Runtime ids of data agents (they share the backend long-running tools). */
export const DATA_AGENT_RUNTIME_IDS = RUNTIME_AGENTS.filter(
  ({ agentName }) => agentName !== ALVIE_COPILOT_AGENT_ID
).map(({ runtimeId }) => runtimeId)

export const isCopilotAgentName = (value: string | null | undefined): value is CopilotAgentName =>
  (COPILOT_AGENT_NAMES as ReadonlyArray<string | null | undefined>).includes(value)

/**
 * Agent a chat reopens on. Production keeps every chat on the surface default, as before the
 * switcher, so a chat from the other surface never swaps the agent this surface's UI is built for.
 */
export const resolveChatAgentName = (surface: CopilotSurface, agentName: string | null | undefined): CopilotAgentName =>
  IS_AGENT_SWITCHER_ENABLED && isCopilotAgentName(agentName) ? agentName : SURFACE_DEFAULT_AGENT[surface]

/** Always-on frontend tools registered for the drawer / page agent. */
export const COPILOT_GLOBAL_PAGE_TOOL_NAMES = [
  'inspect_current_page',
  'highlight_element',
  'get_navigation_target_details',
  'change_url'
] as const
